import React, { useEffect, useMemo, useState } from "react";

import PaginationNumeric from "../../components/PaginationNumeric.jsx";
import { getPastOrders } from "../../api/index.js";
import PastOrdersTable from "../../partials/pastOrders/PastOrdersTable.jsx";
import { useSmallSize } from "../../utils/UseSmallSize.jsx";
import DateSelect from "../../components/DateSelect.jsx";
import moment from "moment";
import { useSelector } from "react-redux";
import { getStableFakeName } from "@/utils/fakeName.js";
import { FaMagnifyingGlass, FaX } from "react-icons/fa6";
import DropdownMulti from "@/components/DropdownMulti.jsx";

function PastOrders() {
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const { selectedLocation, isDemoUser } = useSelector((state) => state.app);
  const [start, setStart] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [end, setEnd] = useState(moment().format("YYYY-MM-DD"));
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [forceReloadTime, setForceReloadTime] = useState(Date.now());
  const [selectedOrderTypes, setSelectedOrderTypes] = useState(["_all"]);
  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when search query changes
  useEffect(() => {
    if (searchQuery !== debouncedSearchQuery) {
      setPage(1);
    }
  }, [searchQuery, debouncedSearchQuery]);

  useEffect(() => {
    getPastOrders(
      page,
      limit,
      start,
      end,
      debouncedSearchQuery,
      selectedOrderTypes
    ).then((res) => {
      if (isDemoUser) {
        res.orders = res.orders.map((order) => {
          const { first_name, last_name } = getStableFakeName(
            order._id,
            !order.direct
          );
          order.customer.first_name = first_name;
          order.customer.last_name = last_name;
          return order;
        });
      }
      setOrders(res.orders);
      setTotalRecords(res.count);
    });
  }, [
    start,
    end,
    page,
    limit,
    selectedLocation,
    debouncedSearchQuery,
    forceReloadTime,
    selectedOrderTypes,
  ]);


  useEffect( () => {
    if (page !== 1) {
      setPage(1);
    } else {
      getPastOrders(
        page,
        limit,
        start,
        end,
        debouncedSearchQuery,
        selectedOrderTypes
      ).then((res) => {
        if (isDemoUser) {
          res.orders = res.orders.map((order) => {
            const { first_name, last_name } = getStableFakeName(
              order._id,
              !order.direct
            );
            order.customer.first_name = first_name;
            order.customer.last_name = last_name;
            return order;
          });
        }
        setOrders(res.orders);
        setTotalRecords(res.count);
      });
    }
  }, [
    start,
    end,
    selectedLocation,
    debouncedSearchQuery,
    forceReloadTime,
    selectedOrderTypes,
  ]);



  const totalPages = useMemo(() => {
    return Math.ceil(totalRecords / limit);
  }, [totalRecords, limit]);


  const isSmallSize = useSmallSize();

  return (
    <div
      className={
        isSmallSize
          ? "px-1 py-2"
          : "px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto"
      }
    >
      {/* Page header */}
      <div
        className={
          isSmallSize
            ? "sm:flex sm:justify-between sm:items-center mb-4"
            : "sm:flex sm:justify-between sm:items-center mb-8"
        }
      >
        {/* Left: Title */}
        <div className={"mb-4 sm:mb-0"}>
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
            Past Orders{" "}
            <span className="text-slate-400 dark:text-slate-500 font-medium">
              {totalRecords}
            </span>
          </h1>
        </div>
        
      </div>
      <div
        className={`flex flex-col sm:flex-row sm:items-center gap-2 flex-1 mb-1`}
      >
        <div className="relative flex-1 max-w-xl">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaMagnifyingGlass className="h-4 w-4 text-slate-400" />
          </div>
          <input
            type="text"
            placeholder="Search by ID / Name / Card Last 4"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input w-full !pl-8 pr-10"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <FaX className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2 w-full sm:w-auto sm:items-center">
          <DateSelect
            onSelect={({ start, end }) => {
              setStart(start);
              setEnd(end);
            }}
          />
          <div className="w-full sm:w-56">
            <DropdownMulti
              options={[
                { id: "_all", label: "All Types" },
                { id: "delivery", label: "Delivery" },
                { id: "pickup", label: "Pick Up" },
                { id: "in-store", label: "In Store" },
              ]}
              selectedValues={selectedOrderTypes}
              setSelectedValues={(values) => {
                const hadAllBefore = selectedOrderTypes.includes("_all");
                const hasAllNow = values.includes("_all");
                const nonAll = values.filter((v) => v !== "_all");

                if (values.length === 0) {
                  // If nothing selected, default back to just "All Types"
                  setSelectedOrderTypes(["_all"]);
                } else if (!hadAllBefore && hasAllNow) {
                  // User just selected "All Types" -> deselect the rest visually
                  setSelectedOrderTypes(["_all"]);
                } else if (hasAllNow && nonAll.length > 0) {
                  // If "All Types" is selected and user picks a specific type, drop "_all"
                  setSelectedOrderTypes(nonAll);
                } else {
                  setSelectedOrderTypes(values);
                }
              }}
              emptyPlaceholder="All Types"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <PastOrdersTable orders={orders} totalRecords={totalRecords} forceReload={() => setForceReloadTime(Date.now())} />

      {/* Pagination */}
      <div className="mt-8">
        <PaginationNumeric
          onSelect={(p) => setPage(p + 1)}
          currentPage={page - 1}
          totalPages={totalPages}
          isFirstPage={page === 1}
          isLastPage={page === totalPages}
        />
      </div>
    </div>
  );
}

export default PastOrders;
